import { Fragment, useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import Button from 'src/components/buttons/button';
import BulletedList from 'src/components/bulletedList';
import NumberedList from 'src/components/numberedList';
import config from 'config/config';
import { ResponsiveImage } from 'src/components/responsiveImage';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}lower-your-cholesterol/`;

const personalisation_mapping = {
  k3: {
    v1: 'YES',
    v2: 'NO',
  },
};

const getMenu = personalisation => {
  const isSmoking = getSmokingPersonalisation(personalisation);
  return isSmoking ? renderMenuForSmokers() : renderMenuForNonSmokers();
};

const renderMenuForNonSmokers = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li>
        <div className="navigator__contents__number">1.</div>
        <div className="navigator__contents__content-text">
          <a href="#1">How stress affects cholesterol</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">2.</div>
        <div className="navigator__contents__content-text">
          <a href="#2">First steps to overcoming stress</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">3.</div>
        <div className="navigator__contents__content-text">
          <a href="#3">Our top tips for reducing stress</a>
        </div>
      </li>
    </ul>
  </section>
);

const renderMenuForSmokers = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li>
        <div className="navigator__contents__number">1.</div>
        <div className="navigator__contents__content-text">
          <a href="#1">How smoking affects cholesterol</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">2.</div>
        <div className="navigator__contents__content-text">
          <a href="#2">The benefits of quitting</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">3.</div>
        <div className="navigator__contents__content-text">
          <a href="#3">5 tips to quit smoking</a>
        </div>
      </li>
      <li>
        <div className="navigator__contents__number">4.</div>
        <div className="navigator__contents__content-text">
          <a href="#4">Support to help you quit</a>
        </div>
      </li>
    </ul>
  </section>
);

const renderContentForNonSmokers = () => {
  return (
    <section className="navigator__content">
      <h1 className="navigator__title">Week 9: How stress could affect your health</h1>
      <p>
        {`Stress is a feeling of being overwhelmed or under pressure. It's normal to feel like that sometimes. But if you
        are feeling like this more and more over time and you are struggling to cope, making some changes will help.`}
      </p>
      <p>
        {`The feeling of stress alone won't give you heart and circulatory diseases. But it is linked to unhealthy habits
        that can increase your risk.`}
      </p>
      <div className="visible-xs">{renderMenuForNonSmokers()}</div>

      <h2>How stress affects your cholesterol</h2>
      <p>
        {`When you're stressed, you may find yourself turning to habits that damage your health. This may be smoking,
        drinking more than is recommended, eating an unhealthy diet or not being active enough. All these things can
        contribute to high cholesterol.`}
      </p>
      <p>
        {`If you're trying to lower your cholesterol and make better habits, tackling your stress levels can help you
        reach your goals. Small, simple changes can have a big impact over time.`}
      </p>
      <a
        href="https://www.bhf.org.uk/heart-health/preventing-heart-disease/stress"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Learn more about stress"
        />
      </a>

      <h2>First steps to overcoming stress</h2>
      <section className="navigator__section-flex">
        <div>
          <p>
            {`Knowing where you are, who you're with and what you're doing when you feel stressed will help shed light on
          what's making you feel that way. You will also notice times when you don't feel stressed and learn to make
          more of them.`}
          </p>
          <p>
            {`For example, if the morning commute makes you feel stressed but walking the dog makes you feel relaxed, try to
          build a short walk into your journey to work each morning. If you're struggling with family problems or
          relationships, the first step should be acknowledging this, staying positive and only trying to change what
          you can control.`}
          </p>
          <a
            href="https://www.bhf.org.uk/heart-matters-magazine/wellbeing/breaking-habits"
            rel="noreferrer"
            target="_blank"
          >
            <Button
              icon="open-right"
              iconLeftAligned={false}
              text="How to break bad habits"
            />
          </a>
        </div>
        <ResponsiveImage
          alt="partially eaten chocolate"
          height={1134}
          src={`${PROGRAM_URL}W09BB02.jpg`}
          width={756}
        />
      </section>

      <h2>Our top tips for reducing stress</h2>
      <BulletedList
        items={[
          <Fragment key="1">
            <strong>Eat a healthy, balanced diet.</strong> This will help lower your cholesterol and help you feel energised throughout the day.
          </Fragment>,
          <Fragment key="2">
            <strong>Make sure you get plenty of rest.</strong>
            Getting just an extra hour of sleep or spending 10 minutes a day doing something relaxing can make an impact on your stress.
          </Fragment>,
          <Fragment key="3">
            <strong>Spend time moving about more.</strong> Being more active can help relieve stress, and it will help lower your cholesterol at the same time.
          </Fragment>,
        ]}
      />
      <a
        href="https://www.bhf.org.uk/heart-matters-magazine/wellbeing/wellbeing-tips"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Find more tips here "
        />
      </a>
      <StyledResponsiveImage
        alt="Content provided by BHF"
        height={117}
        src={`${PROGRAM_URL}bhf-content.jpg`}
        width={828}
      />
    </section>
  );
};

const StyledResponsiveImage = styled(ResponsiveImage)`
  margin-top: 60px;
`;

const renderContentForSmokers = () => {
  return (
    <section className="navigator__content">
      <h1 className="navigator__title">Week 9: How is smoking raising my cholesterol?</h1>
      <p>
        Smokers are much more likely to have high cholesterol and it significantly increases your risk of a heart attack
        or stroke. Quitting smoking is one of the best things you can do to protect your heart and circulatory system.
      </p>
      <div className="visible-xs">{renderMenuForSmokers()}</div>

      <h2>How smoking affects your cholesterol</h2>
      <p>
        Smoking damages the lining of your arteries, leading to a build-up of fatty material (called atheroma) which
        narrows the arteries. Smoking also makes your blood more likely to clot. Both of these can cause a heart attack
        or stroke.
      </p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/y6QJceOAVY0"
        />
      </div>

      <h2>The benefits of quitting</h2>
      <p>
        The good news is that, even if you have smoked for decades, quitting still improves your health in the short and
        long term.
      </p>
      <ResponsiveImage
        alt="The effects of quitting smoking"
        height={1066}
        src={`${PROGRAM_URL}W09AB02.jpg`}
        width={2545}
      />

      <h2>5 tips to quit smoking</h2>
      <NumberedList
        items={[
          <Fragment key="1">
            <strong>Motivate yourself.</strong>{` Write down why you want to quit - in particular how it's going to protect your health - and refer to it throughout the process.`}
          </Fragment>,
          <Fragment key="2">
            <strong>Go public.</strong>{' '}{`Tell the people around you that you're going to quit and get them to encourage you.`}
          </Fragment>,
          <Fragment key="3">
            <strong>Get the right help.</strong>
            Try the NHS Smokefree Quit Kit which contains quitting aids. Your local NHS Stop Smoking Service will also offer one-to-one help.
          </Fragment>,
          <Fragment key="4">
            <strong>Live like a non-smoker.</strong> Get rid of ashtrays and lighters and start thinking of yourself as a non-smoker.
          </Fragment>,
          <Fragment key="5">
            <strong>Do the maths.</strong>{' '}{`Focus on the amount of money you'll save by giving up smoking.`}
          </Fragment>,
        ]}
      />
      <a
        href="https://www.bhf.org.uk/heart-matters-magazine/wellbeing/stop-smoking"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Read how 3 smokers quit"
        />
      </a>

      <h2>Resources to help you quit</h2>
      <p>
        Stopping smoking is probably the single best thing you can do for your health. Your doctor, nurse or pharmacist
        can help you find the resources right for you to help you quit. NHS Smokefree is a good place to start, with
        lots of advice and support to help you stop smoking.
      </p>
      <a
        href="https://www.nhs.uk/smokefree"
        rel="noreferrer"
        target="_blank"
      >
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text="Go to NHS Smokefree"
        />
      </a>
      <ResponsiveImage
        alt="Content provided by BHF"
        className="navigator__banner-footer"
        height={177}
        src={`${PROGRAM_URL}bhf-content.jpg`}
        width={828}
      />
    </section>
  );
};

const getSmokingPersonalisation = personalisation => {
  const { k3 } = personalisation;
  return personalisation_mapping.k3[k3] === 'YES';
};

const getPage = personalisation => {
  const isSmoking = getSmokingPersonalisation(personalisation);
  const content = isSmoking ? renderContentForSmokers() : renderContentForNonSmokers();
  return <div className="navigator">{content}</div>;
};

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <Image
        alt="HealthUnlocked British Heart Foundation community screenshot"
        className="navigator__footer__image"
        height={180}
        src={`${PROGRAM_URL}cta-community.png`}
        width={335}
      />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">Join the conversation on HealthUnlocked</div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on
            HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice
            and encouragement.`}{' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Lower your cholesterol | HealthUnlocked',
};

export default obj;
